/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { CURP, RFC_HOMOCLAVE, RFC_PFAE_SIN_CLAVE } from "@utils/expreciones";
import { useFormik } from "formik";
import * as yup from "yup";
import { ApiError, PersonaStructure } from "@interfaces/response";
import { CurpQuery, PayloadPersonPatch } from "@interfaces/request";
import { RootState, useAppDispatch } from "@store/store";
import { useLazyGetPersonByIdQuery, usePatchPersonMutation } from "@api/personasApi";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { InactivityHandler } from "@components/InactivityHandler";
import CustomRightImage from "@components/CustomRightImagev2";
import * as Sentry from "@sentry/react";
import dayjs from "dayjs";
import { convertSex, extractRFCFromCurp } from "@helpers/funciones";
import { useLazyGetCurpQuery } from "@api/onboarding";

interface AvalAdicionalInfoForm {
  rfc: string;
  homoclave: string;
  curp: string;
  confirm_check: false;
}

const InformacionAvalAdicionalP1 = () => {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const avalAdicionalId = useSelector((state: RootState) => state.bofu.aval_adicional_id_information);

  const [patchPerson] = usePatchPersonMutation();
  const [getPersonById] = useLazyGetPersonByIdQuery();
  const [getCurp] = useLazyGetCurpQuery();

  const [isLoading, setIsLoading] = useState(false);
  const [avalSelected, setAvalSelected] = useState<PersonaStructure | null>(null);

  const formik = useFormik<AvalAdicionalInfoForm>({
    initialValues: {
      rfc: "",
      homoclave: "",
      curp: "",
      confirm_check: false,
    },
    validationSchema: yup.object().shape({
      rfc: yup.string().required("Campo requerido").matches(RFC_PFAE_SIN_CLAVE, "Formato de RFC no válido"),
      homoclave: yup.string().required("Campo requerido").matches(RFC_HOMOCLAVE, "Formato de homoclave no válido"),
      curp: yup.string().required("Campo requerido").matches(CURP, "Formato de CURP no válido"),
      confirm_check: yup.boolean().isTrue().required(""),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        if (!avalSelected) {
          return;
        }

        setSubmitting(true);

        const payload: PayloadPersonPatch = {
          rfc: values.rfc + values.homoclave,
          curp: values.curp,
        };

        await patchPerson({
          personId: avalSelected.id,
          body: payload,
        }).unwrap();

        navigate("/producto/creditosimple/aval_adicional_buro_3");
      } catch (error) {
        const api_error = error as ApiError;

        if (api_error.status === 409) {
          formik.setFieldError("phone", "El número de teléfono ya se encuentra registrado para otro aval");
          return;
        }

        Sentry.captureException(error, {
          extra: {
            component: "InformacionAvalAdicionalP1-submit",
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setSubmitting(false);
      }
    },
    validateOnMount: true,
  });

  const fetchCurp = async (aval_selected: PersonaStructure) => {
    try {
      if (!aval_selected) {
        return;
      }

      setIsLoading(true);
      const birthStateNumber = Number(aval_selected.birth_state);
      const birthStateFormatted = birthStateNumber.toString().padStart(2, "0");

      const payloadGetCurp: CurpQuery = {
        nombres: aval_selected.name,
        apellido_paterno: aval_selected.last_name,
        apellido_materno: aval_selected.last_name_2,
        estado_nacimiento: birthStateFormatted,
        fecha_nacimiento: dayjs(aval_selected.birth_date, "YYYY-MM-DD").format("DD-MM-YYYY"),
        sexo: convertSex(aval_selected.sex),
      };

      const responseGetCurp = await getCurp(payloadGetCurp).unwrap();
      const rfc = extractRFCFromCurp(responseGetCurp.data.curp);

      formik.setFieldValue("curp", responseGetCurp.data.curp);
      formik.setFieldValue("rfc", rfc);
      setTimeout(() => {
        formik.validateForm();
      }, 200);
    } catch (error) {
      const api_error = error as ApiError;
      Sentry.captureException(error, {
        extra: {
          component: "AvalAdicionalBuroP2-fetchCurp",
        },
      });
      dispatch(setModalErrorVisible({ open: true, error: api_error }));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        if (!avalAdicionalId) {
          navigate("/producto/creditosimple/docs_aval");
          return;
        }

        setIsLoading(true);

        const aval_selected = await getPersonById(avalAdicionalId).unwrap();

        // obtener el aval seleccionado, con la informacion actualizada
        // const aval_selected = res_persons.find((item) => item.id === avalAdicionalId);

        // validar si el aval seleccionado existe
        if (!aval_selected) {
          navigate("/producto/creditosimple/docs_aval");
          return;
        }

        setAvalSelected(aval_selected);

        setTimeout(() => {
          fetchCurp(aval_selected);
        }, 300);
      } catch (error) {
        const api_error = error as ApiError;
        Sentry.captureException(error, {
          extra: {
            component: "AvalAdicionalBuroP2-initial_fetch",
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8}>
        <InactivityHandler />
        <Box maxWidth="sm" mx="auto" mt={4} px={2}>
          <Typography
            textAlign="center"
            variant={isMobileOrTablet ? "body2" : "body1"}
            fontWeight={700}
            color={theme?.palette?.primary?.dark}
          >
            Información del aval
          </Typography>

          <Typography
            textAlign={"center"}
            color={theme?.palette?.primary?.main}
            variant={isMobileOrTablet ? "body2" : "body1"}
            fontWeight={600}
            mt={2}
          >
            Te solicitamos ingresar un segundo aval a tu solicitud.
          </Typography>

          <Typography
            mt={2}
            textAlign="center"
            variant={isMobileOrTablet ? "body2" : "body1"}
            color={theme?.palette?.primary?.main}
            fontWeight={600}
          >
            {avalSelected?.name} {avalSelected?.last_name} {avalSelected?.last_name_2}
          </Typography>

          <form onSubmit={formik.handleSubmit} noValidate>
            <Grid container spacing={2}>
              <Grid item xs={8}>
                <FormControl fullWidth>
                  <TextField
                    id="rfc"
                    name="rfc"
                    label="RFC accionista"
                    margin="normal"
                    required
                    fullWidth
                    autoComplete="off"
                    inputProps={{ maxLength: 10 }}
                    onChange={(e) => {
                      const value = (e.target.value || "").toUpperCase();
                      const start = e.target.selectionStart;
                      formik.setFieldValue("rfc", value);
                      setTimeout(() => {
                        e.target.setSelectionRange(start, start);
                      }, 0);
                    }}
                    onBlur={formik.handleBlur}
                    value={formik.values.rfc}
                    error={formik.touched.rfc && Boolean(formik.errors.rfc)}
                    helperText={formik.touched.rfc && formik.errors.rfc}
                    disabled={isLoading || formik.isSubmitting}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                <TextField
                  id="homoclave"
                  name="homoclave"
                  label="Homoclave"
                  margin="normal"
                  required
                  fullWidth
                  autoComplete="off"
                  onChange={(e) => {
                    const value = (e.target.value || "").toUpperCase();
                    const start = e.target.selectionStart;
                    formik.setFieldValue("homoclave", value);
                    setTimeout(() => {
                      e.target.setSelectionRange(start, start);
                    }, 0);
                  }}
                  onBlur={formik.handleBlur}
                  value={formik.values.homoclave}
                  error={formik.touched.homoclave && Boolean(formik.errors.homoclave)}
                  helperText={formik.touched.homoclave && formik.errors.homoclave}
                  inputProps={{ maxLength: 3 }}
                  disabled={isLoading || formik.isSubmitting}
                />
              </Grid>
            </Grid>

            <FormControl fullWidth>
              <TextField
                id="curp"
                name="curp"
                label="CURP"
                margin="normal"
                required
                fullWidth
                autoComplete="off"
                onChange={(e) => {
                  const input = e.target;
                  const value = e.target.value.toUpperCase();
                  const start = input.selectionStart;
                  formik.handleChange(e);
                  formik.setFieldValue("curp", value);
                  setTimeout(() => {
                    input.setSelectionRange(start, start);
                  }, 0);
                }}
                onBlur={formik.handleBlur}
                value={formik.values.curp}
                error={formik.touched.curp && Boolean(formik.errors.curp)}
                helperText={formik.touched.curp && formik.errors.curp}
                inputProps={{ maxLength: 18 }}
                disabled={isLoading || formik.isSubmitting}
              />
            </FormControl>

            <Grid item xs={12} mt={1}>
              <FormControlLabel
                control={<Checkbox id="confirm_check" name="confirm_check" onChange={formik.handleChange} />}
                label="Confirmo que el RFC y CURP son correctos"
              />
            </Grid>

            <Grid container spacing={1} my={1}>
              <Grid item xs={6} sm={6} md={6}>
                <Button
                  variant="blue_outlined"
                  type="button"
                  fullWidth
                  onClick={() => {
                    navigate("/producto/creditosimple/docs_aval");
                  }}
                  disabled={isLoading || formik.isSubmitting}
                >
                  Regresar al inicio
                </Button>
              </Grid>

              <Grid item xs={6} sm={6} md={6}>
                <Button
                  variant="default"
                  type="submit"
                  fullWidth
                  disabled={isLoading || !formik.isValid || formik.isSubmitting}
                >
                  Continuar
                </Button>
              </Grid>
            </Grid>
          </form>
        </Box>
      </Grid>

      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default InformacionAvalAdicionalP1;
